import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Card, CardContent, Grid, Box, Button,
  Switch, FormControlLabel, Divider, Alert, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText, Paper
} from '@mui/material';
import {
  Notifications, Email, Phone, Verified, Work, Save
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import verifierService from '../services/verifierService';
import TierUpgradeDialog from '../components/verifier/TierUpgradeDialog';

const BRAND_GREEN = '#43B581';
const BRAND_DARK = '#27ae60';

const VerifierSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tierUpgradeOpen, setTierUpgradeOpen] = useState(false);

  const [settings, setSettings] = useState({
    isAvailable: true,
    tier: 'general',
    emailNotifications: true,
    emailBidUpdates: true,
    emailAssignments: true,
    smsAlerts: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const profile = await verifierService.getMyProfile();
      setSettings(prev => ({
        ...prev,
        isAvailable: profile.is_available !== false,
        tier: profile.tier || 'general'
      }));
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await verifierService.updateMyProfile({ isAvailable: settings.isAvailable });
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: BRAND_GREEN }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Verifier Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your availability, notifications, and account preferences.
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Availability */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Availability
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isAvailable}
                    onChange={(e) => setSettings(prev => ({ ...prev, isAvailable: e.target.checked }))}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_GREEN },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: BRAND_GREEN } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {settings.isAvailable ? 'Available for new assignments' : 'Not accepting new assignments'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When off, you won't appear in marketplace searches.
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Tier & Upgrade */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>Verifier Tier</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Current tier: <strong>{settings.tier === 'professional' ? 'Professional Verifier' : 'General Verifier'}</strong>
                  </Typography>
                  {settings.tier !== 'professional' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Upgrade to Professional for priority assignments and higher rates.
                    </Typography>
                  )}
                </Box>
                {settings.tier !== 'professional' && (
                  <Button
                    variant="outlined"
                    startIcon={<Verified />}
                    onClick={() => setTierUpgradeOpen(true)}
                    sx={{ borderColor: '#f39c12', color: '#f39c12', whiteSpace: 'nowrap' }}
                  >
                    Request Upgrade
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Preferences */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Notification Preferences
              </Typography>
              <Paper variant="outlined">
                <List disablePadding>
                  <ListItem divider>
                    <ListItemIcon><Email /></ListItemIcon>
                    <ListItemText primary="Email Notifications" secondary="General email updates" />
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => setSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                    />
                  </ListItem>
                  <ListItem divider>
                    <ListItemIcon><Work /></ListItemIcon>
                    <ListItemText primary="New Assignment Alerts" secondary="When a project is assigned to you" />
                    <Switch
                      checked={settings.emailAssignments}
                      onChange={(e) => setSettings(prev => ({ ...prev, emailAssignments: e.target.checked }))}
                    />
                  </ListItem>
                  <ListItem divider>
                    <ListItemIcon><Notifications /></ListItemIcon>
                    <ListItemText primary="Bid & Proposal Updates" secondary="When your proposals receive responses" />
                    <Switch
                      checked={settings.emailBidUpdates}
                      onChange={(e) => setSettings(prev => ({ ...prev, emailBidUpdates: e.target.checked }))}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Phone /></ListItemIcon>
                    <ListItemText primary="SMS Alerts" secondary="Critical updates via text message" />
                    <Switch
                      checked={settings.smsAlerts}
                      onChange={(e) => setSettings(prev => ({ ...prev, smsAlerts: e.target.checked }))}
                    />
                  </ListItem>
                </List>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_DARK } }}
            >
              Save Settings
            </Button>
          </Box>
        </Grid>
      </Grid>

      <TierUpgradeDialog
        open={tierUpgradeOpen}
        onClose={() => setTierUpgradeOpen(false)}
        currentTier={settings.tier}
      />
    </Container>
  );
};

export default VerifierSettingsPage;
